import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import { matchPath, useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { ANY_SPORT_MISSIONS_CATEGORY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import type {
  MissionsCategoryRarityTabsLayoutQuery,
  MissionsCategoryRarityTabsLayoutQueryVariables,
} from './__generated__/index.graphql.ts';

const MISSIONS_CATEGORY_TABS_QUERY = gql`
  query MissionsCategoryRarityTabsLayoutQuery($sport: Sport!) {
    currentUser {
      slug
      id
      taskGroups {
        slug
        myTasks(sport: $sport) {
          id
          aasmState
        }
      }
    }
  }
` as TypedDocumentNode<
  MissionsCategoryRarityTabsLayoutQuery,
  MissionsCategoryRarityTabsLayoutQueryVariables
>;

export const categories = (sport: Sport) => [
  {
    slug: 'play',
    title: <FormattedMessage {...glossary.play} />,
    link: generateSportPath(ANY_SPORT_MISSIONS_CATEGORY, {
      sport,
      params: { category: 'play' },
    }),
  },
  {
    slug: 'collect',
    title: (
      <FormattedMessage
        id="MissionsPage.category.collect"
        defaultMessage="Collect"
      />
    ),
    link: generateSportPath(ANY_SPORT_MISSIONS_CATEGORY, {
      sport,
      params: { category: 'collect' },
    }),
  },
  {
    slug: 'connect',
    title: (
      <FormattedMessage
        id="MissionsPage.category.connect"
        defaultMessage="Connect"
      />
    ),
    link: generateSportPath(ANY_SPORT_MISSIONS_CATEGORY, {
      sport,
      params: { category: 'connect' },
    }),
  },
];

export const MissionsCategoryTabsLayout = ({ children }: PropsWithChildren) => {
  const { sport } = useSportContext();
  const { pathname } = useLocation();
  const { data } = useQuery(MISSIONS_CATEGORY_TABS_QUERY, {
    variables: { sport },
  });
  const { taskGroups } = data?.currentUser || {};

  const allCategories = categories(sport);
  const selectedTab = allCategories.find(({ link }) =>
    matchPath(catchAll(link), pathname)
  )?.slug;

  return (
    <ContentWithStickyHeader
      header={
        <FullWidthContainer.NoPadding>
          <TabBar variant="flat" fullWidth value={selectedTab}>
            {allCategories.map(({ link, title, slug }) => {
              const taskGroup = taskGroups?.find(tg => slug === tg.slug);
              if (!taskGroup) {
                return null;
              }

              const unclaimedTasks = taskGroup.myTasks.filter(
                ({ aasmState }) =>
                  aasmState === ManagerProgressionTaskState.COMPLETED
              );

              return (
                <Tab key={link} value={slug} to={link}>
                  <Badge badgeContent={unclaimedTasks.length}>{title}</Badge>
                </Tab>
              );
            })}
          </TabBar>
        </FullWidthContainer.NoPadding>
      }
    >
      {children}
    </ContentWithStickyHeader>
  );
};
